import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

// API 안내.
//
// 사람이 눌러서 배포하는 길과, 프로그램이 부르는 길은 다르다. 프로그램은
// 로그인 세션이 없으므로 토큰을 쓴다.
//
// 안내와 토큰 만들기를 한자리에 둔다. 다른 데 적어 두면 주소가 바뀔 때
// 문서만 옛날 것으로 남는다. 아래 예시의 주소는 지금 보고 있는 주소에서
// 만들어진다 — 개발에서 보면 개발 주소가 나온다.

// 관리자 화면은 어두운 테마다. 밝은 색을 쓰면 제목이 바탕에 묻혀 아예
// 안 읽힌다 — 실제로 그렇게 만들어 두었다가 브라우저로 띄워 보고 알았다.
const C = {
  text: '#ffffff',
  muted: 'rgba(255,255,255,0.65)',
  dim: 'rgba(255,255,255,0.45)',
  border: 'rgba(139,210,244,0.12)',
  soft: 'rgba(255,255,255,0.04)',
  line: 'rgba(255,255,255,0.10)',
  accent: '#01BCD6',
  ok: '#86efac',
  okBg: 'rgba(34,197,94,0.14)',
  okLine: 'rgba(134,239,172,0.35)',
  warn: '#fbbf24',
  warnBg: 'rgba(251,191,36,0.14)',
  warnLine: 'rgba(251,191,36,0.35)',
  bad: '#fca5a5',
  badLine: 'rgba(248,113,113,0.38)',
  ink: '#0b1626',
};

const Wrap = styled.div`padding: 4px 0 40px; max-width: 900px;`;

const Sec = styled.section`
  margin-bottom: 22px;
  h2 { margin: 0 0 8px; font-size: 15px; color: ${C.text}; }
  p { margin: 0 0 10px; font-size: 13px; color: ${C.muted}; line-height: 1.7; }
  b { color: ${C.text}; }`;

const Code = styled.pre`
  margin: 0 0 10px; padding: 13px 15px; border-radius: 9px;
  background: rgba(0,0,0,0.35); border: 1px solid ${C.line};
  color: #cbd5e1; font-size: 12px; line-height: 1.65;
  overflow: auto; white-space: pre; font-family: 'D2Coding', Consolas, monospace;`;

const Note = styled.p`
  margin: 0 0 10px; padding: 10px 12px; border-radius: 8px;
  background: ${C.warnBg}; border: 1px solid ${C.warnLine}; color: ${C.warn};
  font-size: 12.5px; line-height: 1.7;`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 12.5px; margin-bottom: 10px;
  th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid ${C.line}; }
  th { color: ${C.dim}; font-weight: 600; font-size: 11.5px; }
  td { color: ${C.muted}; }
  code { font-family: Consolas, monospace; font-size: 11.5px; color: ${C.accent};
         background: ${C.soft}; border: 1px solid ${C.line}; border-radius: 4px; padding: 1px 5px; }`;

// ── 토큰 ─────────────────────────────────────────────────────────
const Form = styled.div`
  display: flex; gap: 8px; align-items: flex-end; flex-wrap: wrap; margin-bottom: 12px;
  label { display: flex; flex-direction: column; gap: 5px; font-size: 12px; color: ${C.muted}; }
  input, select { padding: 8px 10px; border: 1px solid ${C.line}; border-radius: 7px;
    font-size: 13px; font-family: inherit; min-width: 170px;
    background: ${C.soft}; color: ${C.text}; }
  input::placeholder { color: ${C.dim}; }
  option { background: #0b1626; color: #fff; }
  button { padding: 9px 16px; border: none; border-radius: 8px; background: ${C.accent};
    color: ${C.ink}; font-family: inherit; font-size: 13px; font-weight: 700; cursor: pointer; }
  button:disabled { background: ${C.soft}; color: ${C.dim}; cursor: not-allowed; }`;

const Fresh = styled.div`
  margin-bottom: 14px; padding: 12px 14px; border-radius: 9px;
  background: ${C.okBg}; border: 1px solid ${C.okLine};
  strong { display: block; font-size: 13px; color: ${C.ok}; margin-bottom: 6px; }
  code { display: block; padding: 9px 11px; background: rgba(0,0,0,0.45); color: ${C.ok};
    border-radius: 7px; font-size: 12.5px; word-break: break-all;
    font-family: 'D2Coding', Consolas, monospace; }
  small { display: block; margin-top: 7px; font-size: 11.5px; color: ${C.ok}; opacity: 0.8; }`;

const Kill = styled.button`
  border: 1px solid ${C.badLine}; background: transparent; color: ${C.bad}; border-radius: 6px;
  padding: 4px 10px; font-size: 11.5px; font-weight: 700; cursor: pointer; font-family: inherit;
  &:disabled { color: ${C.dim}; border-color: ${C.line}; cursor: not-allowed; }`;

const 날짜 = (v) => (v ? String(v).slice(0, 10) : '—');

export default function DeployApi() {
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState('');
  const [scope, setScope] = useState('development');
  const [making, setMaking] = useState(false);
  const [fresh, setFresh] = useState(null);

  // 예시에 적히는 주소는 지금 보고 있는 곳이다. 문서에 박아 두면
  // 개발에서 복사한 명령이 운영을 때린다.
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://antsnest.co.kr';

  const load = useCallback(() => {
    fetch('/api/admin/deploy/tokens', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d?.success) setTokens(d.tokens || []); })
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const make = useCallback(() => {
    if (!name.trim()) return;
    setMaking(true);
    fetch('/api/admin/deploy/tokens', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: name.trim(), scopeTarget: scope || null }),
    })
      .then(r => r.json())
      .then(d => {
        if (d?.success) { setFresh(d.token); setName(''); load(); }
        else window.alert(d?.message || '만들지 못했습니다.');
      })
      .catch(() => window.alert('만들지 못했습니다.'))
      .finally(() => setMaking(false));
  }, [name, scope, load]);

  const kill = useCallback((t) => {
    if (!window.confirm(`'${t.name}' 토큰을 버립니다.\n\n이 토큰을 쓰던 곳은 바로 배포가 막힙니다. 진행할까요?`)) return;
    fetch(`/api/admin/deploy/tokens/${t.id}`, { method: 'DELETE', credentials: 'include' })
      .then(r => r.json()).then(() => load()).catch(() => {});
  }, [load]);

  return (
    <Wrap>
      <Sec>
        <h2>1. 토큰 준비</h2>
        <p>프로그램은 로그인 세션이 없으므로 토큰으로 자신을 밝힙니다.
          <b> 만들 때 딱 한 번만 보여 주고 다시는 볼 수 없습니다</b> — 저장해 두세요.
          서버에는 해시만 남아, DB 가 새도 그 값으로 배포할 수 없습니다.</p>

        <Form>
          <label>
            어디에 쓸 것인지
            <input value={name} onChange={e => setName(e.target.value)}
              placeholder="예: 앤츠톡 CI" maxLength={120} />
          </label>
          <label>
            쓸 수 있는 환경
            <select value={scope} onChange={e => setScope(e.target.value)}>
              <option value="development">개발만</option>
              <option value="production">운영만</option>
              <option value="">둘 다</option>
            </select>
          </label>
          <button type="button" onClick={make} disabled={making || !name.trim()}>
            {making ? '만드는 중…' : '토큰 만들기'}
          </button>
        </Form>

        <Note>
          되도록 <b>개발만</b> 으로 만드세요. CI 가 운영까지 배포할 일은 드물고,
          토큰이 새면 그 범위만큼 열립니다.
        </Note>

        {fresh && (
          <Fresh>
            <strong>{fresh.name} — 지금만 보입니다</strong>
            <code>{fresh.token}</code>
            <small>이 창을 벗어나면 다시 볼 수 없습니다. CI 비밀값에 넣어 두세요.</small>
          </Fresh>
        )}

        <Table>
          <thead>
            <tr><th>이름</th><th>끝자리</th><th>환경</th><th>만든 날</th><th>마지막 사용</th><th /></tr>
          </thead>
          <tbody>
            {tokens.length === 0 && (
              <tr><td colSpan={6} style={{ color: C.dim }}>아직 만든 토큰이 없습니다.</td></tr>
            )}
            {tokens.map(t => (
              <tr key={t.id} style={t.revoked_at ? { opacity: 0.45 } : undefined}>
                <td>{t.name}</td>
                <td><code>{t.hint}</code></td>
                <td>{t.scope_target === 'production' ? '운영만'
                  : t.scope_target === 'development' ? '개발만' : '둘 다'}</td>
                <td>{날짜(t.created_at)}</td>
                <td>{t.last_used_at ? 날짜(t.last_used_at) : '쓴 적 없음'}</td>
                <td>
                  {t.revoked_at
                    ? <span style={{ fontSize: 11.5, color: C.dim }}>버림</span>
                    : <Kill type="button" onClick={() => kill(t)}>버리기</Kill>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sec>

      <Sec>
        <h2>2. 무엇을 배포할 수 있는지 보기</h2>
        <p>서비스마다 지금 올라가 있는 것과 밀린 개수를 돌려줍니다.</p>
        <Code>{`curl -s "${origin}/api/deploy/v1/services?target=development" \\
  -H "Authorization: Bearer $ANTS_DEPLOY_TOKEN"`}</Code>
        <Code>{`{
  "ok": true,
  "target": "development",
  "services": [
    { "project": "AntsTalk", "name": "앤츠톡", "host": "root",
      "current": "aaa1111", "behind": 3, "next": "bbb2222",
      "stages": ["backup","fetch","env","build","restart","health"] }
  ],
  "unreachable": []
}`}</Code>
      </Sec>

      <Sec>
        <h2>3. 배포하기</h2>
        <p>끝날 때까지 기다렸다가 결과를 돌려줍니다. 빌드가 있으면 몇 분 걸립니다.</p>
        <Code>{`curl -s -X POST "${origin}/api/deploy/v1/deploy" \\
  -H "Authorization: Bearer $ANTS_DEPLOY_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"project":"AntsTalk","target":"development","host":"root"}'`}</Code>

        <Table>
          <thead><tr><th>값</th><th>설명</th></tr></thead>
          <tbody>
            <tr><td><code>project</code></td><td>서비스 키. 배포 탭의 <b>자세히</b> 에 나오는 이름입니다.</td></tr>
            <tr><td><code>target</code></td><td><code>development</code> 또는 <code>production</code></td></tr>
            <tr><td><code>host</code></td><td><code>root</code> · <code>master</code> · <code>slave</code>. 안 적으면 <code>root</code></td></tr>
            <tr><td><code>branch</code></td><td>안 적으면 <code>main</code></td></tr>
          </tbody>
        </Table>
      </Sec>

      <Sec>
        <h2>4. 결과 읽기</h2>
        <p><b>성공은 200, 실패는 409 입니다.</b> 실패를 200 으로 주면 CI 가 성공으로 착각합니다.
          어디서 깨졌는지는 <code>failedStage</code> 에 담기니 로그 전문을 뜯지 않아도 됩니다.</p>
        <Code>{`{
  "ok": false,
  "failedStage": "build",
  "reason": ["FAIL 빌드", "Error: Cannot find module 'react-easy-crop'"],
  "seconds": 73,
  "message": "배포하지 못했습니다. 직전 상태로 되돌렸으므로 서비스는 그대로입니다."
}`}</Code>
        <Note>
          실패해도 <b>서비스는 멈추지 않습니다.</b> 받기·빌드·재시작·응답확인 중 하나라도
          어긋나면 직전 상태(커밋 · build · .env · pm2)로 되돌립니다.
        </Note>
      </Sec>

      <Sec>
        <h2>5. 단계</h2>
        <p>서비스마다 도는 단계가 다릅니다. 빌드 스크립트가 없으면 빌드를 건너뛰고,
          pm2 로 안 도는 것은 재시작·응답확인이 없습니다.</p>
        <Table>
          <thead><tr><th>단계</th><th>하는 일</th></tr></thead>
          <tbody>
            <tr><td><code>backup</code></td><td>직전 상태를 떠 둔다(커밋 · build · .env)</td></tr>
            <tr><td><code>fetch</code></td><td>origin 에서 받는다</td></tr>
            <tr><td><code>env</code></td><td>.env 를 되돌린다 — git 추적을 끊어 두어 pull 이 지운다</td></tr>
            <tr><td><code>build</code></td><td>서버에서 빌드한다</td></tr>
            <tr><td><code>restart</code></td><td>pm2 로 올린다. 뜰 때까지 기다린다</td></tr>
            <tr><td><code>health</code></td><td>실제로 응답하는지 본다. pm2 가 online 이어도 안에서 터질 수 있다</td></tr>
          </tbody>
        </Table>
      </Sec>

      <Sec>
        <h2>6. GitHub Actions 예시</h2>
        <Code>{`- name: 개발에 배포
  run: |
    RES=$(curl -s -w '\\n%{http_code}' -X POST \\
      "${origin}/api/deploy/v1/deploy" \\
      -H "Authorization: Bearer \${{ secrets.ANTS_DEPLOY_TOKEN }}" \\
      -H "Content-Type: application/json" \\
      -d '{"project":"AntsTalk","target":"development"}')
    CODE=$(echo "$RES" | tail -1)
    echo "$RES" | head -n -1
    [ "$CODE" = "200" ] || exit 1`}</Code>
      </Sec>

      <Sec>
        <h2>7. 막히면</h2>
        <Table>
          <thead><tr><th>코드</th><th>뜻</th></tr></thead>
          <tbody>
            <tr><td><code>401</code></td><td>토큰이 없거나 버려진 것입니다.</td></tr>
            <tr><td><code>403</code></td><td>그 환경에 쓸 수 없는 토큰입니다(개발만 / 운영만).</td></tr>
            <tr><td><code>400</code></td><td>값이 잘못됐습니다. <code>error</code> 에 어느 값인지 적힙니다.</td></tr>
            <tr><td><code>409</code></td><td>배포가 실패했습니다. 되돌렸으니 서비스는 그대로입니다.</td></tr>
          </tbody>
        </Table>
      </Sec>
    </Wrap>
  );
}
